import React, { useMemo, useState } from 'react';
import { Container, Form, Button, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FaEnvelope, FaWhatsapp } from 'react-icons/fa';

function buildMailto({ ownerEmail, formData }) {
  const subject = `Fahrzeuganfrage: ${formData.make || ''} ${formData.model || ''} ${formData.year || ''}`.trim();
  const bodyLines = [
    'Fahrzeuganfrage',
    '',
    `Marke: ${formData.make || ''}`,
    `Modell: ${formData.model || ''}`,
    `Baujahr: ${formData.year || ''}`,
    `Kilometerstand: ${formData.mileage || ''} km`,
    `Bauform: ${formData.bodyType || ''}`,
    `Kraftstofftyp: ${formData.fuelType || ''}`,
    `PS / kW: ${formData.power || ''}`,
    `Variante: ${formData.variant || ''}`,
    `Getriebeart: ${formData.transmission || ''}`,
    `Türen: ${formData.doors || ''}`,
    `Modelltyp: ${formData.type || ''}`,
    formData.offerPrice ? `Angebot: €${formData.offerPrice}` : '',
    formData.expectedPrice ? `Wunschpreis: €${formData.expectedPrice}` : '',
    '',
    `Zusätzliche Informationen: ${formData.additionalInfo || ''}`,
    '',
    'Kontaktdaten',
    `Name: ${formData.name || ''}`,
    `E-Mail: ${formData.email || ''}`,
    `Telefon: ${formData.phone || ''}`,
  ].filter(Boolean);

  const body = bodyLines.join('\n');
  return `mailto:${encodeURIComponent(ownerEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function EmailContact() {
  const location = useLocation();
  const navigate = useNavigate();

  const carInfoFromHome = location.state?.carInfo || {};

  const [formData, setFormData] = useState({
    make: carInfoFromHome.make || '',
    model: carInfoFromHome.model || '',
    year: carInfoFromHome.year || '',
    mileage: carInfoFromHome.mileage || '',
    bodyType: carInfoFromHome.bodyType || '',
    fuelType: carInfoFromHome.fuelType || '',
    power: carInfoFromHome.power || '',
    variant: carInfoFromHome.variant || '',
    transmission: carInfoFromHome.transmission || '',
    doors: carInfoFromHome.doors || '',
    type: carInfoFromHome.type || '',
    expectedPrice: carInfoFromHome.expectedPrice || '',
    offerPrice: carInfoFromHome.offerPrice || '',
    additionalInfo: '',
    name: '',
    email: '',
    phone: '',

    // Honeypot (should stay empty)
    company: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const ownerEmail = 'auto-zack@hotmail.com';

  const mailtoLink = useMemo(() => buildMailto({ ownerEmail, formData }), [ownerEmail, formData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch('/api/send-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get('content-type') || '';
      let data = null;

      if (contentType.includes('application/json')) {
        data = await response.json().catch(() => null);
      }

      if (!response.ok) {
        // Common case: running CRA dev server via `npm start` (no Vercel functions)
        if (response.status === 404) {
          setError(
            'E-Mail Versand ist lokal (npm start) nicht verfügbar (API 404). Bitte teste auf der Vercel-URL oder starte lokal mit `npm run vercel:dev` (oder `npx vercel dev`).'
          );
          return;
        }

        if (data?.code === 'RESEND_NOT_CONFIGURED') {
          setError('E-Mail Service ist nicht konfiguriert. Bitte setze RESEND_API_KEY (und optional MAIL_FROM/OWNER_EMAIL) in Vercel.');
          return;
        }

        const msg = data?.error || `Senden fehlgeschlagen (HTTP ${response.status}).`;
        setError(msg);
        return;
      }

      navigate('/email-thank-you');
    } catch (err) {
      setError('Netzwerkfehler. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-5">
      <Card className="shadow">
        <Card.Header as="h2" className="text-center">
          Anfrage per E-Mail
        </Card.Header>
        <Card.Body>
          {error ? (
            <Alert variant="danger">
              <div className="d-flex flex-column gap-2">
                <div>{error}</div>
                <div>
                  Wenn das Senden gerade nicht funktioniert, können Sie uns alternativ per WhatsApp kontaktieren
                  oder eine E-Mail über Ihr E-Mail-Programm öffnen:
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Button as={Link} variant="success" to="/whatsapp-contact" state={{ carInfo: carInfoFromHome }}>
                    <FaWhatsapp className="me-2" /> WhatsApp
                  </Button>
                  <Button as="a" variant="outline-primary" href={mailtoLink}>
                    <FaEnvelope className="me-2" /> E-Mail öffnen
                  </Button>
                </div>
              </div>
            </Alert>
          ) : null}

          <Form onSubmit={handleSubmit}>
            <h4 className="mb-3">Fahrzeugdetails</h4>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marke</Form.Label>
                  <Form.Control type="text" name="make" value={formData.make} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Modell</Form.Label>
                  <Form.Control type="text" name="model" value={formData.model} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Baujahr</Form.Label>
                  <Form.Control type="text" name="year" value={formData.year} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Kilometerstand (km)</Form.Label>
                  <Form.Control type="number" name="mileage" value={formData.mileage} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Bauform</Form.Label>
                  <Form.Control type="text" name="bodyType" value={formData.bodyType} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Kraftstofftyp</Form.Label>
                  <Form.Control type="text" name="fuelType" value={formData.fuelType} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Leistung (PS/ kW)</Form.Label>
                  <Form.Control type="text" name="power" value={formData.power} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Variante</Form.Label>
                  <Form.Control type="text" name="variant" value={formData.variant} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Getriebeart</Form.Label>
                  <Form.Control type="text" name="transmission" value={formData.transmission} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Anzahl Türen</Form.Label>
                  <Form.Control type="text" name="doors" value={formData.doors} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Modelltyp</Form.Label>
                  <Form.Control type="text" name="type" value={formData.type} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Wunschpreis</Form.Label>
                  <Form.Control type="number" name="expectedPrice" value={formData.expectedPrice} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            {formData.offerPrice ? (
              <Alert variant="info" className="py-2">
                Berechnetes Angebot: <strong>€{formData.offerPrice}</strong>
              </Alert>
            ) : null}

            <h4 className="mb-3 mt-4">Ihre Kontaktdaten</h4>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ihr Name</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ihre E-Mail</Form.Label>
                  <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Telefon (optional)</Form.Label>
                  <Form.Control type="tel" name="phone" value={formData.phone} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                {/* Honeypot field - keep hidden */}
                <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                  <label htmlFor="company">Company</label>
                  <input id="company" name="company" value={formData.company} onChange={handleChange} autoComplete="off" />
                </div>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Zusätzliche Informationen</Form.Label>
              <Form.Control as="textarea" rows={3} name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button variant="primary" type="submit" size="lg" disabled={submitting}>
                {submitting ? (
                  <span className="d-inline-flex align-items-center gap-2">
                    <Spinner size="sm" /> Senden...
                  </span>
                ) : (
                  <span className="d-inline-flex align-items-center gap-2">
                    <FaEnvelope /> Anfrage per E-Mail senden
                  </span>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
